import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, Col, Flex, Form, Row, Spin } from 'antd';
import { useDebouncedCallback } from 'use-debounce';
import dayjs from 'dayjs';

import DatePickerElement from 'components/datePicker/datePicker';
import StatusComponent from 'components/status/status';
import DisabledElement from 'components/disabledElement/disabledElement';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import VerificationModal from 'modules/main/actions/verificationModal';
import ActionCostComponent from 'modules/main/actions/cost';

import NotificationService from 'core/services/notification';

import { ServiceCost, VerificationPayload } from 'modules/main/actions/commonModels';
import {
	onCheckTreasurySharesLimit,
	onCreateTreasurySharesAcquisition,
	onPrepareAmounts,
	onUpdateTreasurySharesAcquisition,
} from './../services/treasurySharesAcquisition';
import {
	CheckTreasuryShares,
	CreateTreasurySharesAcquisitionPayload,
	PrepareAmountsResponse,
	TreasurySharesAcquisition,
} from '../models/treasurySharesAcquisition';
import { LoadingOutlined } from '@ant-design/icons';
import FormElement from 'components/formElement/formElement';
import { mapResponseErrors } from 'utils/mapResponseErrors';

import styles from './../../../actions.module.scss';
import SelectElement from 'components/selectElement/selectElement';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface TreasurySharesAcquisitionFormProps {
	editMode?: boolean;
	treasurySharesAcquisition?: TreasurySharesAcquisition;
}

const TreasurySharesAcquisitionForm: FunctionComponent<TreasurySharesAcquisitionFormProps> = ({ editMode, treasurySharesAcquisition }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [form] = Form.useForm();

	const [checkingLimit, setCheckingLimit] = useState<boolean>(false);
	const [limitChecked, setLimitChecked] = useState<boolean>(false);
	const [treasuryShares, setTreasuryShares] = useState<CheckTreasuryShares>();

	const [preparingAmounts, setPreparingAmounts] = useState<boolean>(false);
	const [prepareAmountsChecked, setPrepareAmountsChecked] = useState<boolean>(false);
	const [amounts, setAmounts] = useState<PrepareAmountsResponse>();

	const [acknowledge, setAcknowledge] = useState<boolean>(false);
	const [acknowledge2, setAcknowledge2] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	useEffect(() => {
		if (treasurySharesAcquisition) {
			form.setFieldsValue({
				number_of_treasury_shares: treasurySharesAcquisition.action.number_of_treasury_shares,
				revenue_per_share: treasurySharesAcquisition.action.share_price,
				starting_date: dayjs(treasurySharesAcquisition.action.starting_date),
				trading_days: +treasurySharesAcquisition.action.trading_days,
			});

			setAmounts({
				amount: treasurySharesAcquisition.action.amount,
				commission_amount: treasurySharesAcquisition.action.commission_amount,
				vat_amount: treasurySharesAcquisition.action.vat_amount,
				paid_amount: treasurySharesAcquisition.action.paid_amount,
			});
		}
	}, [treasurySharesAcquisition]);

	const checkTreasurySharesLimit = useDebouncedCallback(async (value: number | null) => {
		try {
			if (value === null || value === 0) return;

			setCheckingLimit(true);

			const response = await onCheckTreasurySharesLimit(value);
			setTreasuryShares(response);
			setLimitChecked(true);

			if (form.getFieldValue('revenue_per_share')) prepareAmountHandler();

			setCheckingLimit(false);
		} catch (error: any) {
			setCheckingLimit(false);

			if (error?.status === 422) {
				setTreasuryShares(error?.response?.data?.data);
				setLimitChecked(true);
			}
		}
	}, 1000);

	const prepareAmountHandler = async () => {
		try {
			const treasuryShares = form.getFieldValue('number_of_treasury_shares');
			const price = form.getFieldValue('revenue_per_share');
			if (!treasuryShares || !price) return;

			setPreparingAmounts(true);

			const response = await onPrepareAmounts(treasuryShares, price);
			setAmounts(response);
			setPrepareAmountsChecked(true);

			setPreparingAmounts(false);
		} catch (error: any) {
			setPreparingAmounts(false);

			if (error?.status === 422) {
				setTreasuryShares(error?.response?.data?.data);
				setLimitChecked(true);
			}
		}
	};

	const changeSharePriceHandler = useDebouncedCallback(prepareAmountHandler, 1000);

	const onSubmit = async (data: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload: CreateTreasurySharesAcquisitionPayload = {
				otp: data.key,
				number_of_treasury_shares: form.getFieldValue('number_of_treasury_shares'),
				share_price: form.getFieldValue('revenue_per_share').toString(),
				starting_date: dayjs(form.getFieldValue('starting_date')).format('YYYY-MM-DD'),
				trading_days: form.getFieldValue('trading_days'),
			};

			editMode ? await onUpdateTreasurySharesAcquisition(id!, payload) : await onCreateTreasurySharesAcquisition(payload);

			NotificationService.success(
				t('COMMON.SUCCESS'),
				editMode ? t('ACTIONS.TREASURY_SHARES_ACQUISITION_UPDATED_SUCCESSFULLY') : t('ACTIONS.TREASURY_SHARES_ACQUISITION_CREATED_SUCCESSFULLY'),
			);
			navigate('/dashboard/actions/capitals');
		} catch (error: any) {
			setIsSubmitting(false);
			setVerificationModalOpen(false);

			if (error?.status === 422) mapResponseErrors(form, error);

			const message =
				error?.response?.data?.message ||
				(editMode ? t('ACTIONS.TREASURY_SHARES_ACQUISITION_NOT_UPDATED') : t('ACTIONS.TREASURY_SHARES_ACQUISITION_NOT_CREATED'));

			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return (
		<>
			<Form form={form} onFinish={onSubmit}>
				<div>
					<p className="mb-1">{t('ACTIONS.TOTAL_TREASURY_SHARES_TO_BE_BOUGHT')}</p>
					<Flex align="center" gap={20}>
						<FormNumberElement
							name="number_of_treasury_shares"
							inputClasses="w-120"
							formClasses="mb-0"
							formStyles={{ maxWidth: 'unset' }}
							onChange={checkTreasurySharesLimit}
							step={1}
							min={1}
							addonAfter={
								<Flex gap={20} align="center">
									<p className="mb-0 " style={{ fontSize: 15, marginInlineEnd: 20 }}>
										{t('COMMON.SHARES')}
									</p>
									{limitChecked && (
										<StatusComponent
											bordered
											color={treasuryShares?.valid ? 'green' : 'red'}
											style={{ fontSize: 14, fontWeight: 500 }}
											className="fadeIn"
											text={
												treasuryShares?.valid ? (
													<>
														{t('ACTIONS.CAN_NOT_EXCEED_SUCCEED')}
														<span className={styles.badge}>
															{t('ACTIONS.CURRENT_PERCENTAGE', {
																currentPercentage: treasuryShares?.used_percentage.toFixed(2),
															})}
														</span>
														{t('ACTIONS.REMAINING_SHARES', { remaining: treasuryShares?.remaining?.toLocaleString() })}
													</>
												) : (
													t('ACTIONS.CAN_NOT_EXCEED_FAILED', {
														remaining: treasuryShares?.remaining?.toLocaleString(),
													})
												)
											}
										/>
									)}
								</Flex>
							}
						/>

						{checkingLimit && <Spin indicator={<LoadingOutlined spin style={{ fontSize: 25 }} />} />}
					</Flex>
				</div>

				<Row gutter={[16, 16]} className="mt-3">
					<Col span={24} md={8}>
						<FormNumberElement
							name="revenue_per_share"
							label={t('ACTIONS.PRICE')}
							rules={[
								{ required: true, message: t('COMMON.FIELD_REQUIRED') },
								{
									validator: (_, value) => {
										if (value === null || value === undefined || value <= 0) {
											return Promise.reject(new Error(t('COMMON.FIELD_REQUIRED')));
										}
										return Promise.resolve();
									},
								},
							]}
							disabled={!((limitChecked && treasuryShares?.valid) || editMode)}
							onChange={changeSharePriceHandler}
							precision={2}
							showPrice
						/>
					</Col>
					<Col span={24} md={8}>
						<DatePickerElement
							label={t('ACTIONS.STARTING_DATE')}
							name="starting_date"
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							minDate={dayjs().add(3, 'day')}
							disabled={!((limitChecked && treasuryShares?.valid) || editMode)}
						/>
					</Col>
					<Col span={24} md={8}>
						<SelectElement
							options={[
								{ label: t('ACTIONS.1_DAY'), value: 1 },
								{ label: t('ACTIONS.3_DAYS'), value: 3 },
								{ label: t('ACTIONS.7_DAYS'), value: 7 },
								{ label: t('ACTIONS.15_DAYS'), value: 15 },
								{ label: t('ACTIONS.30_DAYS'), value: 30 },
							]}
							name="trading_days"
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							label={t('ACTIONS.TRADING_DAYS')}
							disabled={!((limitChecked && treasuryShares?.valid) || editMode)}
						/>
					</Col>
				</Row>

				{((prepareAmountsChecked && amounts) || editMode) && (
					<Row gutter={[16, 16]} className="mt-3">
						<Col span={24} md={6}>
							<DisabledElement label={t('ACTIONS.ORDER_AMOUNT')} value={amounts?.amount} loading={preparingAmounts} showPrice />
						</Col>
						<Col span={24} md={6}>
							<DisabledElement label={t('ACTIONS.HEFTH_COMMISSION')} value={amounts?.commission_amount} loading={preparingAmounts} showPrice />
						</Col>
						<Col span={24} md={6}>
							<DisabledElement label={t('ACTIONS.VAT')} value={amounts?.vat_amount} loading={preparingAmounts} showPrice />
						</Col>
						<Col span={24} md={6}>
							<DisabledElement label={t('ACTIONS.TOTAL_AMOUNT_TO_PAID')} value={amounts?.paid_amount} loading={preparingAmounts} showPrice />
						</Col>
					</Row>
				)}

				<Flex wrap gap={20} justify="space-between" align="center" className="mt-5">
					<div>
						<div className="mb-3">
							<Checkbox onChange={(event) => setAcknowledge(event.target.checked)}>
								<span className="fw-semibold d-block">{t('ACTIONS.AGREEMENT_TO_TERMS_CAPITALS')}</span>
								<span className="text-light">{t('MORTGAGE.AGREEMENT_2')}</span>
							</Checkbox>
						</div>
						<div>
							<Checkbox onChange={(event) => setAcknowledge2(event.target.checked)}>
								<span className="fw-semibold d-block">{t('ACTIONS.THREE_DAYS_PAYMENT')}</span>
								<span className="text-light">{t('ACTIONS.FAILURE_TO_PAY_SDAD_BILL')}</span>
							</Checkbox>
						</div>
					</div>

					<Flex gap={20} align="center">
						<SuspendedTooltip>
							<Button
								type="primary"
								className="px-4"
								disabled={!(acknowledge && acknowledge2)}
								onClick={async () => {
									try {
										await form.validateFields();
										setVerificationModalOpen(true);
									} catch (error) {}
								}}>
								{editMode ? t('COMMON.UPDATE') : t('ACTIONS.CREATE_INVOICE')}
							</Button>
						</SuspendedTooltip>
					</Flex>
				</Flex>
			</Form>

			<VerificationModal
				otpOnly
				open={verificationModalOpen}
				isLoading={isSubmitting}
				className="confirmationModal"
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module={editMode ? 'update_capitalRaise_treasury_shares_acquisition' : 'create_capitalRaise_treasury_shares_acquisition'}
				note={t('ACTIONS.TREASURY_SHARES_ACQUISITION_NOTE')}
				title={t('ACTIONS.TREASURY_CONFIRM')}
				subTitle={t('ACTIONS.TREASURY_CONFIRM_SUBTITLE')}
			/>
		</>
	);
};

export default TreasurySharesAcquisitionForm;
